import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Components
import { Button, Col, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import PredictionConfirmModal from './PredictionConfirmModal';
import PredictionWheel from './PredictionWheel';
import {
  findUserVotePrediction,
  getScoreLabel,
  transformPredictionsToSegments
} from './predictionUtils';

/*
  Half-Time prediction dial for the CTA flow — web port of the mobile
  PredictionsSection, reduced to the dial itself: no PREDICTIONS header and no
  half/full-time tabs. Fetches the team's upcoming game and its half_time
  predictions and lets the fan pick a score on the wheel, then vote for real:
  submitting opens a confirmation modal and casts the vote on web. Once voted
  the picked segment stays locked and the fan's score (plus the points the vote
  earned) is shown above the dial.
  Renders nothing when the team has no upcoming game.
*/

const SELECTED_SEGMENT_COLOR = '#27E3DD';
// dark shady green + lighter rim for the submit button while no score is picked yet
const SUBMIT_IDLE_COLOR = '#506d65';
const SUBMIT_IDLE_BORDER = '3px solid #84968d';
const MAX_WHEEL_DIAMETER = 340;

const TeamEmblem = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 16px;
  object-fit: cover;
`;

// Section rule that fades out at both ends so it blends into the blue
// background instead of stopping on a hard edge
const FadeDivider = styled.div`
  width: 100%;
  height: 1px;
  margin: ${(props) => props.marg || '0'};
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.45) 50%,
    rgba(255, 255, 255, 0) 100%
  );
`;

const HubContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 8px;
  z-index: 1;
`;

const PredictionDial = ({ teamId, onVisibilityChange, onVotedChange }) => {
  const { t } = useTranslation();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const [diameter, setDiameter] = useState(MAX_WHEEL_DIAMETER);

  const [game, setGame] = useState(undefined); // undefined = loading, null = no upcoming game
  const [predictions, setPredictions] = useState(null); // { items, userVote, gameState }
  const [loading, setLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(null);

  const [confirmVisible, setConfirmVisible] = useState(false);
  // Points the vote earned. Only the vote response carries them (the getByGame
  // read doesn't), so they're parked in history state — same as the shoutout
  // points on this screen, which is what makes them survive a refresh.
  const [earnedPoints, setEarnedPoints] = useState(state?.predictionPoints || 0);

  useEffect(() => {
    if (teamId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // Fit the wheel to the column (400px on desktop, full width on mobile)
  useLayoutEffect(() => {
    function measure() {
      const width = containerRef.current?.clientWidth;
      if (width) setDiameter(Math.min(width, MAX_WHEEL_DIAMETER));
    }

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  async function load() {
    setLoading(true);

    const upcoming = await Api.get(`/games/getUpcoming?teamId=${teamId}`);
    if (!upcoming?._id) {
      setGame(null);
      setLoading(false);
      return;
    }

    setGame(upcoming);
    await fetchPredictions(upcoming._id);
    setLoading(false);
  }

  async function fetchPredictions(gameId) {
    const res = await Api.get(
      `/games/predictions/getByGame?gameId=${gameId}&teamId=${teamId}&type=half_time`
    );
    if (res?.data) setPredictions(res.data);
  }

  const items = predictions?.items || [];
  const segments = transformPredictionsToSegments(items);
  const votedPrediction =
    findUserVotePrediction(items) ||
    items.find((p) => p._id === predictions?.userVote?.predictionItemId) ||
    null;
  const hasVoted = !!votedPrediction || !!predictions?.userVote;
  const canVote = !!predictions?.gameState?.canVoteHalfTime && !hasVoted;

  // matches the early return below — no upcoming game is the only state that
  // renders nothing (undefined game still shows a spinner)
  const visible = game !== null;

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  // the screen's continue button swaps to the full-time copy once the vote is in
  useEffect(() => {
    onVotedChange?.(hasVoted);
  }, [hasVoted, onVotedChange]);

  const selectedSegment = segments.find((s) => s.id === selectedId) || null;
  const activeSegmentId = votedPrediction?._id || selectedId;

  function handleSegmentClick(segment) {
    if (!canVote) return;
    // toggle: clicking the selected score deselects it
    setSelectedId((id) => (id === segment.id ? null : segment.id));
  }

  // Casts the vote — resolves to true only when it went through, which is what
  // keeps the confirmation modal open on failure
  async function submitVote() {
    const res = await Api.post(
      '/games/predictions/vote',
      { predictionItemId: selectedId, teamId },
      null,
      { silent: true }
    );

    // 400 voting closed / already voted, 404 prediction gone, 5xx (no err text)
    if (!res || res.err || res.status >= 400) return false;

    const points = res?.data?.pointsEarned || 0;
    setEarnedPoints(points);
    // park them in history state so a refresh keeps the points line, the way
    // the shoutout points already do
    navigate(pathname, { replace: true, state: { ...state, predictionPoints: points } });

    // refetch so the item comes back with isUserVote — that's what locks the
    // segment and drives the "Your vote is X-Y" line
    await fetchPredictions(game._id);
    return true;
  }

  function handleVoteSuccess() {
    setConfirmVisible(false);
    setSelectedId(null);
  }

  // No upcoming game for this team — the dial simply doesn't show
  if (game === null) return null;

  if (loading)
    return (
      <Col wid="100%" centerAll noFlex marg="24px 0 0">
        <Spinner2 col={theme.SECONDARY} />
      </Col>
    );

  return (
    <Col wid="100%" centerAll noFlex marg="24px 0 0" ref={containerRef}>
      {/* pre-vote copy only — once the vote is in, the dial collapses to a
          divider + the match line + the placed vote */}
      {!hasVoted && (
        <>
          {segments.length > 0 && (
            <Text b0 col="#fff" center marg="0 0 20px">
              {t('callToAction.earnMorePointsByPrediction')}
            </Text>
          )}

          <Text h5 col="#fff" center>
            {t('callToAction.predictHalfTime')}
          </Text>
        </>
      )}

      {hasVoted && <FadeDivider />}

      {/* match line — team names only, avatars live in the dial hub.
          Equal-flex sides keep "vs" at the true center even when one team
          name is longer than the other. */}
      <Row centerAll noFlex marg="8px 0 0">
        <Text h3 col="#fff" style={{ flex: 1, textAlign: 'center' }}>
          {game?.home?.name}
        </Text>
        <Text b0 col="#fff" style={{ margin: '0 8px' }}>
          vs
        </Text>
        <Text h3 col="#fff" style={{ flex: 1, textAlign: 'center' }}>
          {game?.away?.name}
        </Text>
      </Row>

      {hasVoted && <FadeDivider marg="10px 0 5px" />}

      {/* status line — the fan's placed vote, else the closed-window notice */}
      {votedPrediction ? (
        <>
          <Text b0 bold col="#ffaa3c" center marg="12px 0 0">
            {t('callToAction.yourVoteIs', { score: getScoreLabel(votedPrediction) })}
          </Text>

          {earnedPoints > 0 && (
            <Text b0 col="#fff" center marg="6px 0 0">
              <Trans components={{ highlight: <Text spanned bold col="#FFC93C" /> }}>
                {t('callToAction.predictionPointsEarned', { points: earnedPoints })}
              </Trans>
            </Text>
          )}
        </>
      ) : (
        !canVote &&
        !hasVoted && (
          <Text b0 bold col="orange" center marg="12px 0 0">
            {t('callToAction.halfTimePredictionsClosed')}
          </Text>
        )
      )}

      {/* voted — the wheel is gone, so close the block with a divider and point
          the fan to the app for the full-time score */}
      {hasVoted && (
        <>
          <FadeDivider marg="20px 0 0" />
          <Text bold col="#fff" center marg="20px 0 0">
            {t('callToAction.fullTimePredictionAvaialbleInApp')}
          </Text>
        </>
      )}

      {hasVoted ? null : segments.length ? (
        <Col center centerAll noFlex marg="0 0 15px">
          <PredictionWheel
            segments={segments}
            diameter={diameter}
            activeSegmentId={activeSegmentId}
            activeColor={SELECTED_SEGMENT_COLOR}
            clickable={canVote}
            onSegmentClick={handleSegmentClick}
            centerContent={
              <HubContent>
                {(selectedSegment || votedPrediction) && (
                  <>
                    <Text
                      size="15px"
                      bold
                      center
                      style={{ textTransform: 'uppercase', letterSpacing: 1.1 }}
                    >
                      {t('callToAction.yourPick')}
                    </Text>
                    <Text size="26px" bold center>
                      {selectedSegment?.label ||
                        (votedPrediction ? getScoreLabel(votedPrediction) : '')}
                    </Text>
                  </>
                )}

                {/* equal-flex sides keep "vs" centered even if one emblem is missing */}
                <Row centerAll noFlex>
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    {game?.home?.avatar && <TeamEmblem src={game.home.avatar} alt="" />}
                  </div>
                  <Text size="15px" bold style={{ margin: '0 6px' }}>
                    vs
                  </Text>
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    {game?.away?.avatar && <TeamEmblem src={game.away.avatar} alt="" />}
                  </div>
                </Row>
              </HubContent>
            }
          />
        </Col>
      ) : (
        <Text b0 col="#fff" center marg="16px 0 0">
          {t('callToAction.noPredictionsYet')}
        </Text>
      )}

      {canVote && segments.length > 0 && (
        <Button
          big
          wid="50%"
          marg="16px 0 32px 0"
          bg={selectedSegment ? SELECTED_SEGMENT_COLOR : SUBMIT_IDLE_COLOR}
          textColor={selectedSegment ? theme.PRIMARY_BLACK : '#fff'}
          style={selectedSegment ? undefined : { border: SUBMIT_IDLE_BORDER }}
          text={t('callToAction.submitPrediction')}
          onClick={selectedSegment ? () => setConfirmVisible(true) : undefined}
        />
      )}

      <PredictionConfirmModal
        visible={confirmVisible}
        selectedSegment={selectedSegment}
        game={game}
        onConfirm={submitVote}
        onCancel={() => setConfirmVisible(false)}
        onSuccess={handleVoteSuccess}
      />
    </Col>
  );
};

export default PredictionDial;
